import { useEffect, useMemo } from "react";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { Box, useMediaQuery, useTheme } from "@mui/material";

import {
  PARTNER_SPOTLIGHT_ENTRIES,
  type PartnerSpotlightEntry,
  partnerSpotlightLogoSrc,
} from "config/partnerSpotlightLogos";
import type { Partner } from "types/partners";
import {
  VIEWPORT_2K_MIN_MEDIA,
  VIEWPORT_4K_MIN_MEDIA,
  VIEWPORT_2K_MIN_WIDTH_MATCH,
  VIEWPORT_4K_MIN_WIDTH_MATCH,
} from "config";

/** Tighter strip on 1080p-class viewports (width below VIEWPORT_2K_MIN_WIDTH_PX). */
const SLIDE_GAP_DEFAULT_PX = 12;
const SLIDE_GAP_2K_PX = 28;
const SLIDE_GAP_4K_PX = 32;
const LOGO_BAND_DEFAULT_PX = 34;
const LOGO_BAND_2K_PX = 62;
const LOGO_BAND_4K_PX = 68;

/** Slower advance; avoid permanent stop on scroll/focus (felt like random stops). */
const PARTNER_AUTOPLAY_DELAY_MS = 5200;

type PartnerSpotlightCarouselProps = {
  entries?: PartnerSpotlightEntry[];
  partners?: Partner[];
};

export default function PartnerSpotlightCarousel({
  entries = PARTNER_SPOTLIGHT_ENTRIES,
  partners,
}: PartnerSpotlightCarouselProps) {
  const spotlightEntries = useMemo(() => {
    if (partners && partners.length > 0) {
      return partners.map((partner) => ({
        id: partner.slug,
        file: partner.logo_url,
        alt: partner.name,
        logoDisplayScale: 1,
      }));
    }
    return entries;
  }, [partners, entries]);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLg = useMediaQuery(theme.breakpoints.down("lg"));
  const isViewport2k = useMediaQuery(VIEWPORT_2K_MIN_WIDTH_MATCH);
  const isViewport4k = useMediaQuery(VIEWPORT_4K_MIN_WIDTH_MATCH);

  const slideGapPx = isViewport4k
    ? SLIDE_GAP_4K_PX
    : isViewport2k
      ? SLIDE_GAP_2K_PX
      : SLIDE_GAP_DEFAULT_PX;
  const slideLoopEndPadPx = slideGapPx / 2;
  const logoBandHeightPx = isViewport4k
    ? LOGO_BAND_4K_PX
    : isViewport2k
      ? LOGO_BAND_2K_PX
      : LOGO_BAND_DEFAULT_PX;
  const slideRowPadBelowLogoPx = isViewport4k ? 20 : isViewport2k ? 20 : 8;
  const slideRowMinHeightPx = logoBandHeightPx + slideRowPadBelowLogoPx;

  const slidesInView = useMemo(() => {
    if (isXs) return 2;
    if (isMd) return 3;
    if (isLg) return 4;
    return 6;
  }, [isXs, isMd, isLg]);

  const slideBasisCss = useMemo(
    () =>
      `calc((100% - ${(slidesInView - 1) * slideGapPx}px) / ${slidesInView})`,
    [slidesInView, slideGapPx],
  );

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: PARTNER_AUTOPLAY_DELAY_MS,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
        playOnInit: true,
      }),
    [],
  );

  const carouselAlign = useMemo(() => ({ align: "start" as const }), []);

  const canLoop = spotlightEntries.length > slidesInView;

  const [viewportRef, emblaApi] = useEmblaCarousel(
    { ...carouselAlign, loop: canLoop },
    spotlightEntries.length ? [autoplayPlugin] : [],
  );

  useEffect(() => {
    if (!spotlightEntries.length || !emblaApi) return;
    const loopEnabled = spotlightEntries.length > slidesInView;
    emblaApi.reInit({
      ...carouselAlign,
      loop: loopEnabled,
    });
    queueMicrotask(() => {
      autoplayPlugin.reset();
    });
  }, [
    emblaApi,
    carouselAlign,
    spotlightEntries.length,
    slidesInView,
    slideGapPx,
    logoBandHeightPx,
    slideRowMinHeightPx,
    autoplayPlugin,
  ]);

  if (spotlightEntries.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: { xs: 0.75, sm: 1 },
        [VIEWPORT_2K_MIN_MEDIA]: { mt: 3 },
        [VIEWPORT_4K_MIN_MEDIA]: { mt: 3.5 },
        position: "relative",
        width: "100%",
        minWidth: 0,
        alignSelf: "stretch",
      }}
    >
      <Box
        sx={{ overflow: "hidden", width: "100%", px: { xs: 0, md: 0.25 } }}
        ref={viewportRef}
        role="region"
        aria-roledescription="carousel"
        aria-label="Partner logos"
      >
        <Box
          sx={{
            display: "flex",
            gap: `${slideGapPx}px`,
            touchAction: "pan-y pinch-zoom",
          }}
        >
          {spotlightEntries.map((entry, index) => {
            const scale = entry.logoDisplayScale ?? 1;
            const isFirst = index === 0;
            const isLast = index === spotlightEntries.length - 1;
            return (
              <Box
                aria-roledescription="slide"
                key={entry.id}
                sx={{
                  flex: `0 0 ${slideBasisCss}`,
                  minWidth: 0,
                  flexShrink: 0,
                  boxSizing: "border-box",
                  minHeight: slideRowMinHeightPx,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: { xs: 0.75, md: 0.5 },
                  ...(isFirst && {
                    pl: `calc(${theme.spacing(1)} + ${slideLoopEndPadPx}px)`,
                  }),
                  ...(isLast && {
                    pr: `calc(${theme.spacing(1)} + ${slideLoopEndPadPx}px)`,
                  }),
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: `${logoBandHeightPx}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "visible",
                  }}
                >
                  <Box
                    component="img"
                    src={
                      entry.file.startsWith("http")
                        ? entry.file
                        : partnerSpotlightLogoSrc(entry.file)
                    }
                    alt={entry.alt}
                    loading="eager"
                    draggable={false}
                    sx={{
                      display: "block",
                      width: "100%",
                      maxWidth: "100%",
                      height: `${logoBandHeightPx}px`,
                      maxHeight: `${logoBandHeightPx}px`,
                      objectFit: "contain",
                      objectPosition: "center",
                      transform: scale !== 1 ? `scale(${scale})` : undefined,
                      transformOrigin: "center center",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
